/**
 * Decide when cached Matrix credentials should be refreshed.
 *
 * Matrix CS API treats these as independent:
 * - Access token: HTTP `Authorization` for `/sync`, `/messages`, VoIP signalling.
 * - `since`: the `/sync` batch cursor (`next_batch` → `store.getSyncToken()`).
 *
 * Spec: refresh the access token at any time (even before expiry), then use it on
 * *future* requests and retry failed ones. Do **not** drop `since` or wipe the store
 * just because the token changed — that empties `getRooms()` and tears down
 * `callEventHandler` (matrix-js-sdk `stopClient` does not recreate it on `startClient`).
 *
 * Omit `since` only for a true initial `/sync` (login / long-idle CATCHUP).
 *
 * Spec: https://spec.matrix.org/v1.14/client-server-api/#client-authentication
 * Spec: https://spec.matrix.org/v1.14/client-server-api/#syncing
 */

import { AUTH_TOKEN_KEYS } from '../../constants/common';
import { getLocalItem } from '../../utils/config/localStorage';
import {
  MATRIX_ACCESS_TOKEN_REFRESH_MARGIN_MS,
  MATRIX_ACCESS_TOKEN_TTL_MS,
} from './matrixConstants';

/**
 * @param {string} token
 * @returns {number|null} JWT `exp` in epoch ms, or null if not a JWT
 */
export function readJwtExpiryMs(token) {
  if (!token || typeof token !== 'string') return null;
  const parts = token.split('.');
  if (parts.length < 2) return null;
  try {
    const payload = JSON.parse(atob(parts[1]));
    const exp = Number(payload?.exp);
    if (!Number.isFinite(exp) || exp <= 0) return null;
    return exp * 1000;
  } catch (_) {
    return null;
  }
}

/**
 * @returns {number} epoch ms, or 0 if unknown
 */
export function getMatrixAccessTokenIssuedAt() {
  const raw = getLocalItem(AUTH_TOKEN_KEYS.MATRIX_ACCESS_TOKEN_AT, null);
  if (raw == null) return 0;
  const n = typeof raw === 'number' ? raw : parseInt(String(raw), 10);
  return Number.isFinite(n) && n > 0 ? n : 0;
}

/**
 * True when the cached Matrix token is expired, unknown-age, or inside the refresh margin.
 * @param {number} [now]
 * @param {string|null} [accessToken]
 * @param {{ treatUnknownAgeAsStale?: boolean }} [options]
 *   treatUnknownAgeAsStale: default true for cold boot (`getUserInfo`) so we never
 *   `startClient` with a token that already 401s. A live PREPARED/SYNCING/CATCHUP
 *   client has already proven the token to the HS — pass false and do **not** invent
 *   an issued-at of `Date.now()` (that would hide a token that expires in minutes).
 */
export function isMatrixAccessTokenStale(now = Date.now(), accessToken = null, options = {}) {
  const treatUnknownAgeAsStale = options.treatUnknownAgeAsStale !== false;
  const token =
    accessToken ||
    getLocalItem(AUTH_TOKEN_KEYS.MATRIX_ACCESS_TOKEN, null);
  if (!token || typeof token !== 'string') return true;

  const jwtExp = readJwtExpiryMs(token);
  if (jwtExp != null) {
    return jwtExp - now <= MATRIX_ACCESS_TOKEN_REFRESH_MARGIN_MS;
  }

  const issuedAt = getMatrixAccessTokenIssuedAt();
  if (!issuedAt) return treatUnknownAgeAsStale;

  return now - issuedAt >= MATRIX_ACCESS_TOKEN_TTL_MS - MATRIX_ACCESS_TOKEN_REFRESH_MARGIN_MS;
}

/**
 * Whether to mint a new short-lived access token before `startClient`.
 * Idle `/sync` CATCHUP is a separate decision (`isMatrixSyncStale` / drop `since`).
 * @param {number} [now]
 */
export function shouldRefreshMatrixCredentials(now = Date.now()) {
  return isMatrixAccessTokenStale(now);
}
